/**
 * Owner: Masa Bayu
 * File: face-justify-remeasure.ts
 * Date:Time: 2026-10-05 12:18
 * Description: Face canvas remasure · live-font glue · honest widths · no condense fiction.
 * Rev: 2026-10-12 · Owner Open · fix D1 (kondensasi fiksyen dibuang) + D2 (glue diukur,
 *      bukan 0.25em andaan) · hash D1 di-re-baseline.
 */

import {
  distributeSpacing,
  generateRenderInstructions,
  type RenderInstruction,
  type TextToken,
} from '@qx/spsu-line-break-optimizer';

const PX_PER_PT = 96 / 72;

/** Ideal inter-word glue from the live Face font (pt). */
export function measureSpacePt(fontCss: string): number {
  if (typeof document === 'undefined') return 0;
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return 0;
  ctx.font = fontCss;
  return ctx.measureText(' ').width / PX_PER_PT;
}

/**
 * Ukur lebar perkataan + glue sebenar dari font hidup (pt) · bukan andaan.
 * Lebar glyph tidak boleh dikondensasi secara fiksyen · paint hanya baca margin.
 */
export function remasureAndFitTokensPt(
  tokens: readonly TextToken[],
  breakpoints: readonly number[],
  _constraints: readonly number[],
  _lineWidth: number,
  fontCss: string,
): TextToken[] {
  if (typeof document === 'undefined') return [...tokens];
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return [...tokens];
  ctx.font = fontCss;

  // D2 fix · glue = ruang font sebenar (path sama dengan measureSpacePt) · bukan 0.25em Adapter.
  const spacePt = ctx.measureText(' ').width / PX_PER_PT;
  const measured: TextToken[] = tokens.map((token, i) => {
    const isLast = breakpoints.includes(i);
    return {
      ...token,
      width: ctx.measureText(token.text).width / PX_PER_PT,
      spaceAfter: isLast ? 0 : spacePt,
    };
  });

  // D1 fix · tiada kondensasi glyph: jika natural baris > constraint · biarkan
  // distributeSpacing kecilkan glue (floor ≥ 0 di faceJustifyInstructions).
  // Lebar model kini == lebar paint · overflow yang tinggal ialah isyarat jujur
  // font substitusi / pemutusan baris · bukan distorsi senyap.
  // Params _constraints/_lineWidth kekal untuk kestabilan API · digunakan oleh
  // distributeSpacing di faceJustifyInstructions.
  return measured;
}

export function breakpointsFromInstructions(
  instructions: readonly RenderInstruction[],
): number[] {
  const out: number[] = [];
  for (let i = 0; i < instructions.length; i += 1) {
    if (instructions[i]!.isLastInLine) out.push(i);
  }
  return out;
}

/**
 * Remasure → redistribute → RenderInstruction[] untuk Face.
 * Floor: margin bukan-last ≥ 0 · negative glue = perkataan bertindih dalam paint.
 */
export function faceJustifyInstructions(
  tokens: readonly TextToken[],
  planInstructions: readonly RenderInstruction[],
  constraints: readonly number[],
  lineWidth: number,
  fontCss: string,
): RenderInstruction[] {
  if (tokens.length === 0) return [];
  const breakpoints = breakpointsFromInstructions(planInstructions);
  const lineConstraints =
    constraints.length === breakpoints.length
      ? [...constraints]
      : breakpoints.map(() => lineWidth);
  const measured = remasureAndFitTokensPt(
    tokens,
    breakpoints,
    lineConstraints,
    lineWidth,
    fontCss,
  );
  const adjustments = distributeSpacing({
    tokens: measured,
    breakpoints,
    constraints: lineConstraints,
    lineWidth,
  });
  return generateRenderInstructions(measured, adjustments, breakpoints).map(
    (inst) =>
      inst.isLastInLine
        ? inst
        : { ...inst, marginRightPx: Math.max(0, inst.marginRightPx) },
  );
}
